import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Nav,
  Tab,
} from 'react-bootstrap';
import { FaSearch, FaFileExcel, FaEraser } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import Tabela from '~/Components/TabelaDinamica/Tabela';
import {
  buscarGruposEmpresaChequesDevolvidos,
  buscarCedentesChequesDevolvidos,
  consultarChequesDevolvidos,
} from '~/Services/Service';
import {
  extrairMensagemErro,
  exportarEmExcel,
  formatarMonetario,
  formatarTimestamp,
} from '~/Utils/Helpers';

// Backend desta demanda usa `SimpleMessage.Create` que serializa `{ "mensagem": "..." }`,
// ao passo que o padrão geral do projeto é `.message`. Aceitamos ambos.
const mensagemErro = (err, fallback) => {
  const m1 = err && err.message ? err.message : null;
  const m2 = err && err.mensagem ? err.mensagem : null;
  return m1 || m2 || (typeof err === 'string' ? err : null) || extrairMensagemErro(err) || fallback;
};

const TIPOS_FILTRO = [
  { key: 'cedente', label: 'Por Cedente' },
  { key: 'banco', label: 'Por Banco' },
  { key: 'vendedor', label: 'Por Vendedor' },
  { key: 'operador', label: 'Por Operador' },
];

const OPCOES_SITUACAO = [
  { value: 'N', label: 'Não Baixados' },
  { value: 'S', label: 'Baixados' },
  { value: '', label: 'Todos' },
];

const formatarData = (valor) => {
  if (!valor) return '-';
  const d = new Date(valor);
  if (isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// Bancos/agências/contas/cheque vêm como decimal (numeric(19,6)) — exibir como inteiro.
const formatarInteiro = (valor) => {
  if (valor == null) return '-';
  const n = Number(valor);
  if (isNaN(n)) return '-';
  return String(Math.trunc(n));
};

// DW-0963: CgcCpf agora chega como string (ChequeDevolvidoItemResponse.CgcCpf
// número→string). Normaliza preservando letras (uppercase) — sem \D-strip nem
// Number()/Math.trunc (que apagariam A-Z / gerariam NaN→'-'). CPF (11) segue
// numérico; CNPJ (14) pode ser alfanumérico. Espelha somenteAlfanumerico de
// validationSchemas.js (toUpperCase + [^A-Z0-9]).
const formatarCgcCpf = (valor) => {
  if (valor == null) return '-';
  const doc = String(valor).toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (doc.length === 11) {
    return doc.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  if (doc.length === 14) {
    return doc.replace(
      /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  }
  return doc || '-';
};

const COLUNAS = [
  { key: 'conta_cedente', title: 'Cedente', ordenacao: true, align: 'right' },
  {
    key: 'banco_cheque_rec',
    title: 'Banco',
    ordenacao: true,
    align: 'right',
    render: formatarInteiro,
  },
  {
    key: 'agencia_cheque_rec',
    title: 'Agência',
    ordenacao: true,
    align: 'right',
    render: formatarInteiro,
  },
  {
    key: 'numero_cheque_rec',
    title: 'Nº Cheque',
    ordenacao: true,
    align: 'right',
    render: formatarInteiro,
  },
  {
    key: 'conta_cheque_rec',
    title: 'Conta',
    ordenacao: true,
    align: 'right',
    render: formatarInteiro,
  },
  { key: 'banco_apresentacao', title: 'Bc. Apr.', ordenacao: true, align: 'right' },
  {
    key: 'valor',
    title: 'Valor',
    ordenacao: true,
    align: 'right',
    render: (v) => (v != null ? formatarMonetario(Number(v), false) : '-'),
  },
  {
    key: 'data_baixa',
    title: 'Dt. Baixa',
    ordenacao: true,
    render: (v) => formatarData(v),
  },
  {
    key: 'cgc_cpf',
    title: 'CGC/CPF',
    ordenacao: true,
    align: 'right',
    render: formatarCgcCpf,
  },
  {
    key: 'data_1_dev',
    title: '1ª Dev.',
    ordenacao: true,
    render: (v) => formatarData(v),
  },
  { key: 'alinea_1_dev', title: 'Alín. 1', ordenacao: true, align: 'right' },
  {
    key: 'data_1_rep',
    title: '1ª Reap.',
    ordenacao: true,
    render: (v) => formatarData(v),
  },
  {
    key: 'data_2_dev',
    title: '2ª Dev.',
    ordenacao: true,
    render: (v) => formatarData(v),
  },
  { key: 'alinea_2_dev', title: 'Alín. 2', ordenacao: true, align: 'right' },
  {
    key: 'data_pagamento',
    title: 'Dt. Pag.',
    ordenacao: true,
    render: (v) => formatarData(v),
  },
  { key: 'forma_pagamento', title: 'F.P.', ordenacao: true },
  { key: 'nome', title: 'Nome Emitente', ordenacao: true },
];

const VALORES_INICIAIS = {
  codigoGe: null,
  cedenteIni: '',
  cedenteFim: '',
  bancoIni: '',
  bancoFim: '',
  codigoVendedor: '',
  codigoOperador: '',
  dataIni: '',
  dataFim: '',
  situacaoBaixa: 'N',
};

const buildSchema = (tipoFiltro) => {
  const base = {
    dataIni: Yup.date().typeError('Data Inicial inválida').required('Data Inicial é obrigatória'),
    dataFim: Yup.date()
      .typeError('Data Final inválida')
      .required('Data Final é obrigatória')
      .min(Yup.ref('dataIni'), 'Data Final deve ser maior ou igual à Data Inicial'),
    situacaoBaixa: Yup.string().oneOf(['N', 'S', '']).required('Situação obrigatória'),
  };

  if (tipoFiltro === 'cedente') {
    base.cedenteIni = Yup.string().required('Cedente Inicial é obrigatório');
    base.cedenteFim = Yup.string().required('Cedente Final é obrigatório');
  } else if (tipoFiltro === 'banco') {
    base.bancoIni = Yup.number()
      .typeError('Banco Inicial inválido')
      .required('Banco Inicial é obrigatório');
    base.bancoFim = Yup.number()
      .typeError('Banco Final inválido')
      .required('Banco Final é obrigatório')
      .min(Yup.ref('bancoIni'), 'Banco Final deve ser maior ou igual ao Banco Inicial');
  } else if (tipoFiltro === 'vendedor') {
    base.codigoVendedor = Yup.number()
      .typeError('Vendedor inválido')
      .required('Vendedor é obrigatório');
  } else if (tipoFiltro === 'operador') {
    base.codigoOperador = Yup.number()
      .typeError('Operador inválido')
      .required('Operador é obrigatório');
  }

  return Yup.object().shape(base);
};

const ChequesDevolvidos = () => {
  const [gruposEmpresa, setGruposEmpresa] = useState([]);
  const [cedenteOptions, setCedenteOptions] = useState([]);
  const [carregandoCedentes, setCarregandoCedentes] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('cedente');
  const [geInicial, setGeInicial] = useState(null);

  // Carrega GEs do usuário no mount; auto-seleciona quando há somente 1 (paridade SetaGE do ERP)
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const res = await buscarGruposEmpresaChequesDevolvidos();
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : (res && res.data) || [];
        setGruposEmpresa(lista);
        if (lista.length === 1) {
          const unicoGe = lista[0].codigo_grupo_empresa;
          setGeInicial(unicoGe);
          carregarCedentesInterno(unicoGe);
        }
      } catch (err) {
        if (!ativo) return;
        toast.error(mensagemErro(err, 'Erro ao carregar Grupos de Empresa.'));
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregamento direto (sem dependência de Formik) para o auto-select inicial
  const carregarCedentesInterno = (codigoGe) => {
    if (codigoGe == null) {
      setCedenteOptions([]);
      return;
    }
    let ativo = true;
    setCarregandoCedentes(true);
    setCedenteOptions([]);
    (async () => {
      try {
        const res = await buscarCedentesChequesDevolvidos(codigoGe);
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : (res && res.data) || [];
        setCedenteOptions(lista);
      } catch (err) {
        if (!ativo) return;
        toast.error(mensagemErro(err, 'Erro ao carregar cedentes do GE.'));
      } finally {
        if (ativo) setCarregandoCedentes(false);
      }
    })();
  };

  const opcoesGE = useMemo(
    () =>
      gruposEmpresa.map((g) => ({
        value: g.codigo_grupo_empresa,
        label: `${g.codigo_grupo_empresa} - ${g.apelido}`,
      })),
    [gruposEmpresa]
  );

  const cedentesSelect = useMemo(
    () =>
      cedenteOptions.map((c) => ({
        value: c.codigo_conta,
        label: `${c.codigo_conta} - ${c.apelido_conta}`,
      })),
    [cedenteOptions]
  );

  const carregarCedentes = (codigoGe, setFieldValue) => {
    if (setFieldValue) {
      setFieldValue('cedenteIni', '');
      setFieldValue('cedenteFim', '');
    }
    carregarCedentesInterno(codigoGe);
  };

  const limparCamposDeAba = (aba, setFieldValue) => {
    if (aba !== 'cedente') {
      setFieldValue('cedenteIni', '');
      setFieldValue('cedenteFim', '');
    }
    if (aba !== 'banco') {
      setFieldValue('bancoIni', '');
      setFieldValue('bancoFim', '');
    }
    if (aba !== 'vendedor') setFieldValue('codigoVendedor', '');
    if (aba !== 'operador') setFieldValue('codigoOperador', '');
  };

  const enviarConsulta = async (values) => {
    setLoading(true);
    try {
      const payload = {
        tipo_filtro: abaAtiva,
        data_ini: values.dataIni,
        data_fim: values.dataFim,
        codigo_ge: values.codigoGe != null ? Number(values.codigoGe) : null,
        situacao_baixa: values.situacaoBaixa || '',
        cedente_ini: values.cedenteIni ? Number(values.cedenteIni) : null,
        cedente_fim: values.cedenteFim ? Number(values.cedenteFim) : null,
        banco_ini: values.bancoIni !== '' ? Number(values.bancoIni) : null,
        banco_fim: values.bancoFim !== '' ? Number(values.bancoFim) : null,
        codigo_vendedor:
          values.codigoVendedor !== '' ? Number(values.codigoVendedor) : null,
        codigo_operador:
          values.codigoOperador !== '' ? Number(values.codigoOperador) : null,
      };
      const res = await consultarChequesDevolvidos(payload);
      const dados = res && res.data ? res.data : res;
      setResultado(dados || { registros: [], total_quantidade: 0, total_valor: 0 });
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao consultar cheques devolvidos.'));
      setResultado(null);
    } finally {
      setLoading(false);
    }
  };

  const dadosComKey = useMemo(() => {
    if (!resultado || !Array.isArray(resultado.registros)) return [];
    return resultado.registros.map((r) => ({ ...r, id: r.codigo_titulo }));
  }, [resultado]);

  const totalQuantidade = resultado ? resultado.total_quantidade || 0 : 0;
  const totalValor = resultado ? resultado.total_valor || 0 : 0;
  const temResultado = dadosComKey.length > 0;

  const handleExportarExcel = () => {
    if (!temResultado) {
      toast.warning('Nenhum dado para exportar.');
      return;
    }
    const cabecalhos = COLUNAS.map((c) => c.title);
    const linhas = dadosComKey.map((item) =>
      COLUNAS.map((coluna) => {
        if (typeof coluna.render === 'function') {
          const valor = coluna.render(item[coluna.key], item);
          if (valor && typeof valor === 'object') return item[coluna.key];
          return valor != null ? valor : '';
        }
        return item[coluna.key] != null ? item[coluna.key] : '';
      })
    );
    const ts = formatarTimestamp(new Date());
    exportarEmExcel([cabecalhos, ...linhas], `Cheques_Devolvidos_${ts}.xlsx`);
  };

  return (
    <Container fluid className="py-3">
      <Header title="Cheques Devolvidos" />

      <Formik
        initialValues={{ ...VALORES_INICIAIS, codigoGe: geInicial }}
        enableReinitialize
        validationSchema={buildSchema(abaAtiva)}
        validateOnBlur
        onSubmit={enviarConsulta}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldValue,
          resetForm,
        }) => {
          const trocarAba = (novaAba) => {
            if (novaAba === abaAtiva) return;
            limparCamposDeAba(novaAba, setFieldValue);
            setAbaAtiva(novaAba);
          };

          const limparTudo = () => {
            resetForm({ values: { ...VALORES_INICIAIS, codigoGe: geInicial } });
            setAbaAtiva('cedente');
            setResultado(null);
            if (geInicial != null) carregarCedentesInterno(geInicial);
            else setCedenteOptions([]);
          };

          return (
            <Form noValidate onSubmit={handleSubmit}>
              <Card className="mt-3">
                <Card.Body>
                  <Row className="align-items-end">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Grupo de Empresa</Form.Label>
                        <Select
                          options={opcoesGE}
                          value={
                            opcoesGE.find((o) => o.value === values.codigoGe) || null
                          }
                          onChange={(opt) => {
                            const novoGe = opt ? opt.value : null;
                            setFieldValue('codigoGe', novoGe);
                            carregarCedentes(novoGe, setFieldValue);
                          }}
                          placeholder="Todos os GEs do usuário"
                          isClearable
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mt-3">
                <Card.Body>
                  <Tab.Container activeKey={abaAtiva} onSelect={trocarAba}>
                    <Nav variant="tabs">
                      {TIPOS_FILTRO.map((t) => (
                        <Nav.Item key={t.key}>
                          <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>

                    <Tab.Content className="pt-3">
                      <Tab.Pane eventKey="cedente">
                        <Row>
                          <Col xs={12} md={6}>
                            <Form.Group>
                              <Form.Label>Cedente Inicial</Form.Label>
                              <Select
                                options={cedentesSelect}
                                value={
                                  cedentesSelect.find(
                                    (o) => String(o.value) === String(values.cedenteIni)
                                  ) || null
                                }
                                onChange={(opt) =>
                                  setFieldValue('cedenteIni', opt ? opt.value : '')
                                }
                                isLoading={carregandoCedentes}
                                isDisabled={values.codigoGe == null}
                                placeholder={
                                  values.codigoGe == null
                                    ? 'Selecione um GE primeiro'
                                    : 'Selecione o cedente inicial'
                                }
                                isClearable
                              />
                              {touched.cedenteIni && errors.cedenteIni && (
                                <Form.Text className="text-danger">
                                  {errors.cedenteIni}
                                </Form.Text>
                              )}
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Group>
                              <Form.Label>Cedente Final</Form.Label>
                              <Select
                                options={cedentesSelect}
                                value={
                                  cedentesSelect.find(
                                    (o) => String(o.value) === String(values.cedenteFim)
                                  ) || null
                                }
                                onChange={(opt) =>
                                  setFieldValue('cedenteFim', opt ? opt.value : '')
                                }
                                isLoading={carregandoCedentes}
                                isDisabled={values.codigoGe == null}
                                placeholder={
                                  values.codigoGe == null
                                    ? 'Selecione um GE primeiro'
                                    : 'Selecione o cedente final'
                                }
                                isClearable
                              />
                              {touched.cedenteFim && errors.cedenteFim && (
                                <Form.Text className="text-danger">
                                  {errors.cedenteFim}
                                </Form.Text>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      </Tab.Pane>

                      <Tab.Pane eventKey="banco">
                        <Row>
                          <Col xs={6} md={3}>
                            <Form.Group>
                              <Form.Label>Banco Inicial</Form.Label>
                              <Form.Control
                                type="number"
                                name="bancoIni"
                                value={values.bancoIni}
                                onChange={handleChange}
                                placeholder="Ex: 001"
                                isInvalid={touched.bancoIni && !!errors.bancoIni}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.bancoIni}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col xs={6} md={3}>
                            <Form.Group>
                              <Form.Label>Banco Final</Form.Label>
                              <Form.Control
                                type="number"
                                name="bancoFim"
                                value={values.bancoFim}
                                onChange={handleChange}
                                placeholder="Ex: 999"
                                isInvalid={touched.bancoFim && !!errors.bancoFim}
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.bancoFim}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Tab.Pane>

                      <Tab.Pane eventKey="vendedor">
                        <Row>
                          <Col xs={12} md={4}>
                            <Form.Group>
                              <Form.Label>Código do Vendedor</Form.Label>
                              <Form.Control
                                type="number"
                                name="codigoVendedor"
                                value={values.codigoVendedor}
                                onChange={handleChange}
                                placeholder="Código do vendedor"
                                isInvalid={
                                  touched.codigoVendedor && !!errors.codigoVendedor
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.codigoVendedor}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Tab.Pane>

                      <Tab.Pane eventKey="operador">
                        <Row>
                          <Col xs={12} md={4}>
                            <Form.Group>
                              <Form.Label>Código do Operador</Form.Label>
                              <Form.Control
                                type="number"
                                name="codigoOperador"
                                value={values.codigoOperador}
                                onChange={handleChange}
                                placeholder="Código do operador"
                                isInvalid={
                                  touched.codigoOperador && !!errors.codigoOperador
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.codigoOperador}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Tab.Pane>
                    </Tab.Content>
                  </Tab.Container>

                  <hr />

                  <Row>
                    <Col xs={6} md={3}>
                      <Form.Group>
                        <Form.Label>Data Inicial</Form.Label>
                        <Form.Control
                          type="date"
                          name="dataIni"
                          value={values.dataIni}
                          onChange={handleChange}
                          isInvalid={touched.dataIni && !!errors.dataIni}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.dataIni}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={6} md={3}>
                      <Form.Group>
                        <Form.Label>Data Final</Form.Label>
                        <Form.Control
                          type="date"
                          name="dataFim"
                          value={values.dataFim}
                          onChange={handleChange}
                          isInvalid={touched.dataFim && !!errors.dataFim}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.dataFim}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Label>Situação</Form.Label>
                      <div>
                        {OPCOES_SITUACAO.map((op) => (
                          <Form.Check
                            inline
                            key={op.value || 'todos'}
                            type="radio"
                            id={`situacao-${op.value || 'todos'}`}
                            name="situacaoBaixa"
                            label={op.label}
                            value={op.value}
                            checked={values.situacaoBaixa === op.value}
                            onChange={() =>
                              setFieldValue('situacaoBaixa', op.value)
                            }
                          />
                        ))}
                      </div>
                      {touched.situacaoBaixa && errors.situacaoBaixa && (
                        <Form.Text className="text-danger">
                          {errors.situacaoBaixa}
                        </Form.Text>
                      )}
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col>
                      <Button
                        type="submit"
                        variant="primary"
                        className="mr-2"
                        disabled={loading}
                      >
                        <FaSearch className="mr-1" /> Gerar Relatório
                      </Button>
                      <Button variant="secondary" onClick={limparTudo} disabled={loading}>
                        <FaEraser className="mr-1" /> Limpar
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {loading && <Loading label="Consultando cheques devolvidos..." />}

              {!loading && resultado && (
                <Card className="mt-3">
                  <Card.Body>
                    <Row className="align-items-center mb-3">
                      <Col xs={12} md="auto" className="mb-2 mb-md-0">
                        <Button
                          variant="success"
                          onClick={handleExportarExcel}
                          disabled={!temResultado}
                        >
                          <FaFileExcel className="mr-1" /> Exportar Excel
                        </Button>
                      </Col>
                      <Col xs={12} md className="text-md-right">
                        <strong>
                          Total: {totalQuantidade} cheque(s) | Valor Total:{' '}
                          {formatarMonetario(Number(totalValor) || 0, false)}
                        </strong>
                      </Col>
                    </Row>

                    <div style={{ overflowX: 'auto' }}>
                      <Tabela colunas={COLUNAS} dados={dadosComKey} />
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default ChequesDevolvidos;
